import type { FC } from 'react';
import { useCallback } from 'react';
import { Marker, useMap } from 'react-map-gl';

import { DonutClusterMarker } from './DonutClusterMarker';
import { useClusterData } from './use-cluster-data';

interface ClusterMarkersProps {
  sourceId?: string;
}

export const ClusterMarkers: FC<ClusterMarkersProps> = ({
  sourceId = 'data-studies',
}) => {
  const { current: mapRef } = useMap();
  const clusters = useClusterData(mapRef ?? null, sourceId);

  const handleClusterClick = useCallback(
    (clusterId: number, longitude: number, latitude: number) => {
      if (!mapRef) return;

      const map = mapRef.getMap();
      const source = map.getSource(sourceId);

      if (!source || source.type !== 'geojson') {
        return;
      }

      // Get the zoom level to expand this cluster
      source.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) {
          console.error('Error getting cluster expansion zoom:', err);
          return;
        }

        // Zoom to the cluster's expansion zoom level
        map.easeTo({
          center: [longitude, latitude],
          zoom: zoom,
          duration: 500,
        });
      });
    },
    [mapRef, sourceId],
  );

  return (
    <>
      {clusters.map(cluster => (
        <Marker
          key={cluster.id}
          longitude={cluster.longitude}
          latitude={cluster.latitude}
          anchor="center"
          onClick={e => {
            e.originalEvent.stopPropagation();
            handleClusterClick(cluster.id, cluster.longitude, cluster.latitude);
          }}
        >
          <DonutClusterMarker
            designCounts={cluster.designCounts}
            totalCount={cluster.totalCount}
          />
        </Marker>
      ))}
    </>
  );
};
